import { v4 as uuidv4 } from 'uuid'
import type { Bet, BetslipView } from '@nsftx/seven-client-retail-vue-integrator'
import { getBetPayoutAmount, getBetslipPayinTotal } from '@nsftx/seven-client-retail-vue-sgp'
import type { Voucher } from '@nsftx/seven-client-retail-vue-sgp'
import { storeToRefs } from 'pinia'
import { useTokenPayloadStore } from '@nsftx/seven-client-retail-vue-sgp'
import { useCalculatorStore } from '@/stores/calculator/calculatorStore'
import type { CalculatorActionType, CalculatorEntryStatus } from './types'

const resolveCalculatorCurrency = (currency?: string) => {
  const { baseCurrency } = storeToRefs(useTokenPayloadStore())

  return currency || baseCurrency.value || 'EUR'
}

const resolveItemLabel = (linkCode: string) => linkCode

const getBetPayinAmount = (bet: Bet) => Number.parseFloat(bet.payin.total)

const buildBetTransactionId = (betslipId: string, betId: string) => `${betslipId}:${betId}`

const signedAmountForAction = (action: CalculatorActionType, absoluteAmount: number) => {
  const paymentActions: CalculatorActionType[] = [
    'betslip_payin',
    'voucher_create',
    'direct_deposit',
  ]

  if (paymentActions.includes(action)) {
    return absoluteAmount
  }

  return -absoluteAmount
}

export const recordCalculatorTransaction = (input: {
  currency: string
  action: CalculatorActionType
  amount: number
  item: string
  transactionId: string
  status?: CalculatorEntryStatus
  timestamp?: number
}) => {
  const calculatorStore = useCalculatorStore()
  const currency = resolveCalculatorCurrency(input.currency)

  if (!calculatorStore.supportedCurrencies.includes(currency)) {
    return
  }

  const status = input.status ?? 'success'

  calculatorStore.addEntry({
    id: uuidv4(),
    included: true,
    item: input.item,
    transactionId: input.transactionId,
    timestamp: input.timestamp ?? Date.now(),
    action: input.action,
    amount: signedAmountForAction(input.action, Math.abs(input.amount)),
    status,
    currency,
  })
}

export const recordBetslipPayinTransaction = (betslip: BetslipView) => {
  const calculatorStore = useCalculatorStore()
  const currency = resolveCalculatorCurrency(betslip.context.currency)
  const amount = getBetslipPayinTotal(betslip)

  if (!Number.isFinite(amount) || amount <= 0) {
    return
  }

  recordCalculatorTransaction({
    currency,
    action: 'betslip_payin',
    amount,
    item: resolveItemLabel(betslip.context.linkCode),
    transactionId: betslip.id,
    status: 'success',
  })

  calculatorStore.currency = currency
}

export const recordBetslipPayoutTransaction = (
  betslipId: string,
  bet: Bet,
  currency: string,
  linkCode: string,
) => {
  const calculatorStore = useCalculatorStore()
  const payoutAmount = getBetPayoutAmount(bet)

  if (!Number.isFinite(payoutAmount) || payoutAmount <= 0) {
    return
  }

  recordCalculatorTransaction({
    currency: resolveCalculatorCurrency(currency),
    action: 'betslip_payout',
    amount: payoutAmount,
    item: resolveItemLabel(linkCode),
    transactionId: buildBetTransactionId(betslipId, bet.id),
    status: 'success',
  })

  calculatorStore.currency = resolveCalculatorCurrency(currency)
}

export const recordBetslipCancelTransaction = (
  betslipId: string,
  bet: Bet,
  currency: string,
  linkCode: string,
) => {
  const amount = getBetPayinAmount(bet)

  if (!Number.isFinite(amount) || amount <= 0) {
    return
  }

  recordCalculatorTransaction({
    currency: resolveCalculatorCurrency(currency),
    action: 'betslip_cancel',
    amount,
    item: resolveItemLabel(linkCode),
    transactionId: buildBetTransactionId(betslipId, bet.id),
    status: 'success',
  })
}

export const recordBetslipCashoutTransaction = (
  betslipId: string,
  bet: Bet,
  currency: string,
  linkCode: string,
) => {
  const amount = getBetPayoutAmount(bet)

  if (!Number.isFinite(amount) || amount <= 0) {
    return
  }

  recordCalculatorTransaction({
    currency: resolveCalculatorCurrency(currency),
    action: 'betslip_cashout',
    amount,
    item: resolveItemLabel(linkCode),
    transactionId: buildBetTransactionId(betslipId, bet.id),
    status: 'success',
  })
}

export const recordVoucherCreateTransaction = (
  amount: number,
  currency: string,
  voucherId: string,
) => {
  recordCalculatorTransaction({
    currency,
    action: 'voucher_create',
    amount,
    item: 'Voucher',
    transactionId: voucherId,
    status: 'success',
  })
}

export const recordVoucherRedeemTransaction = (voucher: Voucher) => {
  recordCalculatorTransaction({
    currency: voucher.context.currency,
    action: 'voucher_redeem',
    amount: voucher.context.amount,
    item: 'Voucher',
    transactionId: voucher.id,
    status: 'success',
  })
}

export const recordVoucherRevokeTransaction = (voucher: Voucher) => {
  recordCalculatorTransaction({
    currency: voucher.context.currency,
    action: 'voucher_revoke',
    amount: voucher.context.amount,
    item: 'Voucher',
    transactionId: voucher.id,
    status: 'success',
  })
}
